<template>
  <div>
    <div v-if="!loading && order" class="ov">
      <!-- ═══════════ Kopfzeile ═══════════ -->
      <div class="ov-head">
        <div>
          <div class="ov-head__row">
            <h1 class="ov-head__name">{{ order.orderName }}</h1>
            <span class="ov-status" :class="'ov-status--' + (order.status || '').toLowerCase()">
              {{ order.status }}
            </span>
            <span v-if="order.fulfillHold" class="ov-hold-chip" :title="holdTooltip">
              <v-icon x-small color="#b26a00">mdi-pause-circle</v-icon> HOLD
            </span>
          </div>
          <div class="ov-head__sub">
            {{ getDateFromNow(order.orderDate) }} · {{ getDate(order.orderDate) }}
            · {{ order.shopId }}_{{ order.countryCode }}
          </div>
        </div>
        <v-spacer />
        <div class="ov-head__actions">
          <v-btn outlined rounded @click="openOrderInShopify">
            <v-icon small left>mdi-open-in-new</v-icon> Shopify
          </v-btn>
          <v-btn outlined rounded @click="openLooxReviews">
            <v-icon small left>mdi-star-outline</v-icon> Loox
          </v-btn>
          <v-btn outlined rounded @click="copyOrderStatusUrl">
            <v-icon small left>mdi-link-variant</v-icon> Status-Link
          </v-btn>
          <v-btn outlined rounded @click="showChecklistDialog = true">
            <v-icon small left>mdi-checkbox-marked-outline</v-icon> Checkliste
          </v-btn>
          <v-btn
            v-if="showFulfillOptions"
            rounded
            :outlined="!order.fulfillHold"
            :depressed="!!order.fulfillHold"
            :dark="!!order.fulfillHold"
            :color="order.fulfillHold ? '#f0a92e' : undefined"
            :loading="holdSaving"
            @click="toggleFulfillHold"
          >
            <v-icon small left>{{ order.fulfillHold ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline' }}</v-icon>
            {{ order.fulfillHold ? 'Hold aufheben' : 'Hold' }}
          </v-btn>
          <v-btn v-if="showFulfillOptions" dark rounded depressed @click="openManualFulfillmentDialog">
            <v-icon small left>mdi-package-variant-closed</v-icon> Manual Fulfill
          </v-btn>
          <v-btn v-if="showFulfillOptions" outlined rounded @click="openReproduceDialog">
            <v-icon small left>mdi-restart</v-icon> Reproduce
          </v-btn>
          <v-btn outlined rounded color="error" @click="() => (showCancelOrderDialog = true)">
            <v-icon small left>mdi-cancel</v-icon> Cancel
          </v-btn>
        </div>
      </div>

      <v-row class="mt-2">
        <v-col cols="12" md="8">
          <!-- ═══════════ Fulfillments ═══════════ -->
          <div v-if="order.fulfillment">
            <div
              class="ov-card"
              v-for="fulfillerId in Object.keys(order.fulfillment)"
              :key="fulfillerId"
            >
              <div class="ov-card__head">
                <v-icon small class="ov-card__icon">mdi-truck-outline</v-icon>
                {{ fulfillerId }} Fulfillment
              </div>
              <div
                class="ov-fulfillment"
                :key="fulfillment.fulfiller_id"
                v-for="fulfillment in order.fulfillment[fulfillerId]"
              >
                <div class="ov-fulfillment__meta">
                  <span
                    class="ov-chip"
                    :class="fulfillment.pending ? 'ov-chip--wait' : fulfillment.success ? 'ov-chip--ok' : 'ov-chip--bad'"
                  >
                    {{ fulfillment.pending ? "pending" : fulfillment.success ? "ok" : "FAILED" }}
                  </span>
                  <span class="ov-table__mono">{{ fulfillment.fulfiller_id }}</span>
                  <span class="ov-fulfillment__date">
                    {{ getDateFromNow(fulfillment.creationDate) }} · {{ getDate(fulfillment.creationDate) }}
                  </span>
                </div>
                <table class="ov-table">
                  <thead>
                    <tr>
                      <th>Variant</th><th>Menge</th><th>Datei</th><th>Produziert</th>
                      <th>Versandt</th><th>Tracking</th><th>Carrier</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fulfillment.fulfillItems" :key="item.name">
                      <td>{{ item.variant }} {{ item.express ? "(EXPRESS)" : "" }}</td>
                      <td>{{ item.quantity }}</td>
                      <td>
                        <a v-if="item.file" :href="item.file" target="_blank">Datei</a>
                        <span v-else>—</span>
                      </td>
                      <td>{{ item.produced ? "Ja" : "Nein" }}</td>
                      <td>
                        {{ item.shipment ? "Ja" : "Nein" }}
                        <span v-if="item.shipment && item.shipment.shippingDate" class="ov-fulfillment__date">
                          ({{ getDateFromNow(item.shipment.shippingDate) }})
                        </span>
                      </td>
                      <td>
                        <a v-if="item.shipment" :href="item.shipment.trackingUrl" target="_blank">
                          {{ item.shipment.trackingCode }}
                        </a>
                        <span v-else>—</span>
                      </td>
                      <td>{{ item.shipment ? item.shipment.carrier : "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- ═══════════ Tasks ═══════════ -->
          <div class="ov-card">
            <div class="ov-card__head">
              <v-icon small class="ov-card__icon">mdi-image-multiple-outline</v-icon>
              {{ order.tasks.length }} Tasks
            </div>
            <OrderTasks :key="order.localUpdateKey" :order-tasks="order.tasks" :order-id="order.uid" />
          </div>

          <!-- ═══════════ Vom Kunden ersetzte Tasks (Änderungslink) ═══════════ -->
          <div v-if="(order.artworkChangedByCustomer || []).length" class="ov-card">
            <div class="ov-card__head">
              <v-icon small class="ov-card__icon">mdi-history</v-icon>
              Vom Kunden ersetzt: {{ order.artworkChangedByCustomer.length }} {{ order.artworkChangedByCustomer.length === 1 ? "Vorgänger" : "Vorgänger" }}
              <span class="ov-card__sub">nicht mehr Teil der Bestellung, wird nicht produziert</span>
            </div>
            <ReplacedTasks :changes="order.artworkChangedByCustomer" />
          </div>

          <!-- ═══════════ Alle Artworks des Kunden (ohmyart-ai, über User-Hash) ═══════════ -->
          <div class="ov-card">
            <div class="ov-card__head">
              <v-icon small class="ov-card__icon">mdi-palette-outline</v-icon>
              Alle Artworks des Kunden
              <span v-if="customerArtworks" class="ov-artworks__count">{{ customerArtworks.length }}</span>
              <v-spacer />
              <v-btn v-if="!customerArtworks" small rounded depressed :loading="artworksLoading" @click="loadCustomerArtworks">
                <v-icon small left>mdi-image-search-outline</v-icon> Laden
              </v-btn>
              <v-btn v-else small icon :loading="artworksLoading" title="Neu laden" @click="loadCustomerArtworks"><v-icon small>mdi-refresh</v-icon></v-btn>
            </div>
            <div v-if="artworksError" class="ov-hint ov-hint--error">{{ artworksError }}</div>
            <div v-else-if="!customerArtworks" class="ov-hint">Zeigt alles, was der Kunde im Editor generiert hat, auch nicht bestellte Bilder. Lädt erst auf Klick.</div>
            <div v-else-if="!customerArtworks.length" class="ov-hint">Keine generierten Artworks zu diesem Kunden gefunden.</div>
            <div v-else class="ov-artworks">
              <button
                v-for="a in customerArtworks"
                :key="a.id"
                type="button"
                class="ov-artwork"
                :class="{ 'ov-artwork--ordered': a.ordered, 'ov-artwork--muted': a.deleted || a.missing }"
                :disabled="!a.url"
                :title="artworkTitle(a)"
                @click="showArtwork(a)"
              >
                <img v-if="a.url" :src="a.url" alt="" loading="lazy" />
                <div v-else class="ov-artwork__missing"><v-icon small>mdi-image-off-outline</v-icon></div>
                <span v-if="a.ordered" class="ov-artwork__badge ov-artwork__badge--ordered">bestellt</span>
                <span v-else-if="a.deleted" class="ov-artwork__badge">gelöscht</span>
                <span v-else-if="a.missing" class="ov-artwork__badge">Datei weg</span>
                <span class="ov-artwork__meta">
                  <b>{{ a.styleId || "?" }}</b>
                  <em>{{ a.createdAt ? getDateFromNow(new Date(a.createdAt)) : "" }}</em>
                </span>
              </button>
            </div>
          </div>

        </v-col>

        <v-col cols="12" md="4">
          <!-- ═══════════ Customer ═══════════ -->
          <div class="ov-card">
            <div class="ov-card__head">
              <v-icon small class="ov-card__icon">mdi-account-outline</v-icon>
              Customer
            </div>
            <div class="ov-customer__name">{{ order.customer.firstName }}</div>
            <div v-if="!isEditEmail" class="ov-customer__email">
              <span>{{ order.customer.email }}</span>
              <v-btn icon x-small @click="copyEmailToClipboard"><v-icon small>mdi-content-copy</v-icon></v-btn>
              <v-btn icon x-small @click="() => editEmail(true)"><v-icon small>mdi-pencil</v-icon></v-btn>
            </div>
            <div class="d-flex mt-2 align-center" v-else>
              <v-text-field dense outlined hide-details label="Customer E-Mail" v-model="customEmail" />
              <v-btn :loading="isSavingEmail" @click="saveCustomEmail" icon><v-icon>mdi-content-save</v-icon></v-btn>
              <v-btn @click="() => editEmail(false)" icon><v-icon>mdi-close-circle</v-icon></v-btn>
            </div>

            <!-- Weitere Bestellungen dieses Kunden -->
            <div class="ov-subhead">
              Weitere Bestellungen
              <v-progress-circular v-if="loadingCustomerOrders" indeterminate size="12" width="2" class="ml-1" />
            </div>
            <div v-if="!loadingCustomerOrders && !customerOrders.length" class="ov-hint">
              Keine weiteren Bestellungen mit dieser E-Mail.
            </div>
            <div
              v-for="co in customerOrders"
              :key="co.uid"
              class="ov-order-link"
              @click="openCustomerOrder(co)"
            >
              <span class="ov-order-link__name">{{ co.orderName }}</span>
              <span class="ov-status ov-status--sm" :class="'ov-status--' + (co.status || '').toLowerCase()">
                {{ co.status }}
              </span>
              <span class="ov-order-link__date">{{ getDateFromNow(co.orderDate) }}</span>
            </div>
          </div>

          <!-- ═══════════ Versandadresse ═══════════ -->
          <div class="ov-card">
            <div class="ov-card__head">
              <v-icon small class="ov-card__icon">mdi-map-marker-outline</v-icon>
              Versandadresse
              <v-spacer />
              <v-btn icon small @click="showEditOrderAddressDialog = true"><v-icon small>mdi-pencil</v-icon></v-btn>
            </div>
            <div class="ov-address">
              <p v-if="order.shippingAddress.company">{{ order.shippingAddress.company }}</p>
              <p>{{ order.shippingAddress.firstName }} {{ order.shippingAddress.lastName }}</p>
              <p>{{ order.shippingAddress.addressPrimary }}</p>
              <p v-if="order.shippingAddress.addressSecondary">{{ order.shippingAddress.addressSecondary }}</p>
              <p>{{ order.shippingAddress.postCode }} {{ order.shippingAddress.city }}</p>
              <p>{{ order.shippingAddress.province }} {{ order.shippingAddress.countryCode }}</p>
            </div>
            <EditOrderAddressDialog
              :show="showEditOrderAddressDialog"
              :order="order"
              :on-close="closeEditOrderAddressDialog"
            />
          </div>
        </v-col>
      </v-row>
    </div>
    <div v-else style="width: 100vh; height: 100vh">
      <StateDisplay :loading="loading" loading-message="Load Order" />
    </div>

    <ConfirmDialog
      :loading="isCanceling"
      icon="mdi-alert"
      message="Are you sure you want to Cancel the entire order?"
      :show="showCancelOrderDialog"
      :on-accept="cancelOrder"
      :on-cancel="() => (showCancelOrderDialog = false)"
    />
    <ManualFulfillDialog
      v-if="order && order.uid"
      :order-id="order.uid"
      :onClose="closeManualFulfillmentDialog"
      :show="showManualFulfillmentDialog"
    />
    <ReproduceOrderDialog
      v-if="order && order.uid && showReproduceDialog"
      :order-id="order.uid"
      :onClose="closeReproduceDialog"
      :show="showReproduceDialog"
    />

    <!-- Aufgabe zur Team-Checkliste (Header) — Order wird automatisch verlinkt -->
    <v-dialog v-model="showChecklistDialog" max-width="440">
      <v-card class="pa-4">
        <div class="text-subtitle-1 font-weight-bold mb-1">
          Zur Checkliste: {{ order && order.orderName }}
        </div>
        <v-text-field
          v-model="checklistText"
          dense
          outlined
          hide-details
          autofocus
          placeholder="Was ist zu tun? z. B. „Bitte Vorschau prüfen“"
          @keydown.enter="submitChecklistItem"
        />
        <v-select
          v-model="checklistAssignee"
          :items="checklistAssigneeItems"
          dense
          outlined
          hide-details
          class="mt-2"
          label="Zuweisen an"
        />
        <div class="d-flex justify-end mt-3" style="gap: 8px">
          <v-btn text rounded @click="showChecklistDialog = false">Abbrechen</v-btn>
          <v-btn
            rounded
            depressed
            color="#7b5cd6"
            dark
            :loading="checklistSaving"
            :disabled="!checklistText.trim()"
            @click="submitChecklistItem"
          >
            <v-icon small left>mdi-plus</v-icon> Anlegen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <ImageDialog v-if="showImageDialog" :image="imageToShow" :show="showImageDialog" :on-close="() => (showImageDialog = false)" />
  </div>
</template>

<script>
import { auth, db } from "../../fb";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import StateDisplay from "@/components/StateDisplay";
import OrderTasks from "@/components/AllOrders/components/OrderTasks";
import ReplacedTasks from "@/components/AllOrders/components/ReplacedTasks";
import { addChecklistItem, copyToClipboard, getDateFromNow, getOrderStatusUrl, openOrderInShopify } from "@/utils";
import EditOrderAddressDialog from "@/components/FulfillOrders/components/EditOrderAddressDialog";
import ImageDialog from "@/components/ImageDialog";
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../fb";
import ManualFulfillDialog from "@/components/FulfillOrders/components/ManualFulfillDialog";
import { orderStatus, permissions, shopifyNameByShopId, taskStatus } from "@/constants";
import ConfirmDialog from "@/components/ConfirmDialog";
import dayjs from "dayjs";
import { mapGetters } from "vuex";
import ReproduceOrderDialog from "@/components/FulfillOrders/components/ReproduceOrderDialog";
import { EventBus, events } from "../../event-bus";

export default {
  name: "OrderView",
  components: {
    ImageDialog,
    ReplacedTasks,
    ReproduceOrderDialog,
    ConfirmDialog,
    ManualFulfillDialog,
    EditOrderAddressDialog,
    OrderTasks,
    StateDisplay,
  },
  data() {
    return {
      isEditEmail: "",
      customEmail: "",
      unsubscribe: null,
      order: null,
      loading: false,
      showEditOrderAddressDialog: false,
      showManualFulfillmentDialog: false,
      showCancelOrderDialog: false,
      showReproduceDialog: false,
      isCanceling: false,
      isSavingEmail: false,
      // Weitere Bestellungen des Kunden
      customerOrders: [],
      loadingCustomerOrders: false,
      // Team-Checkliste
      showChecklistDialog: false,
      checklistText: "",
      checklistAssignee: window.localStorage.getItem("td_checklist_default_assignee") || "",
      checklistSaving: false,
      holdSaving: false,
      /** Alle generierten Artworks des Kunden (ohmyart-ai) — null = noch nicht geladen */
      customerArtworks: null,
      artworksLoading: false,
      artworksError: "",
      showImageDialog: false,
      imageToShow: "",
    };
  },
  computed: {
    ...mapGetters(["userPermissions", "allUsers"]),
    checklistAssigneeItems() {
      return [
        { text: "👥 Alle", value: "" },
        ...(this.allUsers || [])
          .filter((u) => u.username)
          .map((u) => ({ text: u.username, value: u.uid })),
      ];
    },
    showFulfillOptions() {
      return (
        this.userPermissions.includes(permissions.ADMIN) ||
        this.userPermissions.includes(permissions.FULFILL_ORDERS)
      );
    },
    holdTooltip() {
      const h = this.order?.fulfillHold;
      if (!h) return "";
      const who = h.by?.name || "?";
      const when = h.at ? getDateFromNow(new Date(h.at)) : "";
      return `Fulfillment angehalten von ${who} ${when}`;
    },
  },
  created() {
    this.loadOrder();
  },
  watch: {
    // Navigation zu einer anderen Order derselben Route (z.B. über
    // "Weitere Bestellungen") — Komponente wird wiederverwendet
    "$route.params.orderUid"() {
      if (this.unsubscribe) this.unsubscribe();
      this.order = null;
      this.customerOrders = [];
      this.loadOrder();
    },
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    /** Hold an/aus: blockiert Auto- und Scheduler-Fulfill, bis er aufgehoben
     *  wird (Anzeige auch in FulfillOrders mit Pause-Symbol) */
    async toggleFulfillHold() {
      if (this.holdSaving) return;
      this.holdSaving = true;
      try {
        const next = this.order.fulfillHold
          ? null
          : {
              at: Date.now(),
              by: { uid: auth.currentUser?.uid || null, name: auth.currentUser?.displayName || "" },
            };
        await setDoc(doc(db, "orders", this.order.uid), { fulfillHold: next }, { merge: true });
      } catch (e) {
        console.error("Hold umschalten fehlgeschlagen", e);
      } finally {
        this.holdSaving = false;
      }
    },
    /**
     * Alle Artworks des Kunden laden: User-Hash und Customization-IDs aus den Tasks der Order,
     * dann ohmyart-ai über den Proxy themePreviewAction (Action userArtworks) fragen.
     */
    async loadCustomerArtworks() {
      if (this.artworksLoading) return;
      this.artworksLoading = true;
      this.artworksError = "";
      try {
        const userIds = new Set();
        const customizationIds = new Set();
        for (const t of this.order?.tasks || []) {
          if (!t?.taskUid) continue;
          const snap = await getDoc(doc(collection(db, "tasks"), t.taskUid));
          const cd = snap.exists() ? snap.data()?.customizationData || {} : {};
          if (cd.userId) userIds.add(cd.userId);
          if (cd.id) customizationIds.add(cd.id);
        }
        if (!userIds.size) {
          this.customerArtworks = [];
          this.artworksError = "Kein User-Hash an den Tasks dieser Order (kein Live-Editor-Task).";
          return;
        }
        const fn = httpsCallable(fbFunctions, "themePreviewAction", { timeout: 120000 });
        const { data } = await fn({ action: "userArtworks", payload: { userIds: [...userIds], customizationIds: [...customizationIds] } });
        this.customerArtworks = (data && data.artworks) || [];
      } catch (e) {
        console.error("Artworks des Kunden laden fehlgeschlagen", e);
        this.artworksError = "Laden fehlgeschlagen: " + (e?.message || e);
      } finally {
        this.artworksLoading = false;
      }
    },
    artworkTitle(a) {
      const parts = [a.styleId || "unbekannter Stil"];
      if (a.createdAt) parts.push(dayjs(a.createdAt).format("DD.MM.YYYY HH:mm"));
      if (a.engineId) parts.push(a.engineId);
      if (a.ordered) parts.push("in dieser Bestellung");
      if (a.deleted) parts.push("vom Kunden gelöscht");
      if (a.missing) parts.push("Datei nicht mehr im Storage");
      return parts.join(" · ");
    },
    showArtwork(a) {
      if (!a.url) return;
      this.imageToShow = a.url;
      this.showImageDialog = true;
    },
    /** Aufgabe mit dieser Order verlinkt in die Team-Checkliste legen */
    async submitChecklistItem() {
      const text = this.checklistText.trim();
      if (!text || this.checklistSaving) return;
      this.checklistSaving = true;
      try {
        const assignee = (this.allUsers || []).find((u) => u.uid === this.checklistAssignee);
        await addChecklistItem({
          text,
          orderUid: this.order?.uid || null,
          orderName: this.order?.orderName || null,
          assignedTo: assignee?.uid || null,
          assignedToName: assignee?.username || null,
        });
        window.localStorage.setItem("td_checklist_default_assignee", this.checklistAssignee || "");
        this.checklistText = "";
        this.showChecklistDialog = false;
      } catch (e) {
        console.error("Checklist: Anlegen fehlgeschlagen", e);
      } finally {
        this.checklistSaving = false;
      }
    },
    async loadOrder() {
      this.loading = true;
      const orderUid =
        this.$route.params.orderUid || this.$route.query.orderUid;
      const orderDoc = doc(db, `/orders/${orderUid}`);
      let first = true;
      this.unsubscribe = onSnapshot(orderDoc, (snapshot) => {
        this.order = {
          localUpdateKey: Date.now(),
          ...snapshot.data(),
        };
        if (first) {
          first = false;
          this.loadCustomerOrders();
        }
      });
      this.loading = false;
    },

    /* ── Weitere Bestellungen des Kunden ── */
    async loadCustomerOrders() {
      const email = this.order?.customer?.email;
      if (!email) return;
      this.loadingCustomerOrders = true;
      try {
        const q = query(
          collection(db, "orders"),
          where("customer.email", "==", email),
          limit(25)
        );
        const snap = await getDocs(q);
        this.customerOrders = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((o) => o.uid !== String(this.order.uid))
          .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
      } catch (e) {
        console.warn("customer orders load failed:", e);
      } finally {
        this.loadingCustomerOrders = false;
      }
    },
    openCustomerOrder(co) {
      this.$router.push(`/order/${co.uid}`);
    },

    /* ── Loox ── */
    openLooxReviews() {
      const sub =
        shopifyNameByShopId[this.order.shopId]?.[this.order.countryCode || "DE"];
      const email = encodeURIComponent(this.order.customer.email || "");
      window.open(
        `https://admin.shopify.com/store/${sub}/apps/loox-fashion-reviews/merchant/VkZs_igG0n/settings/orders?end_date=&q=${email}&range=&start_date=&status=`,
        "_blank"
      );
    },

    /* ── Bestellstatus-Link für den Kunden ── */
    copyOrderStatusUrl() {
      const url = getOrderStatusUrl({
        shopId: this.order.shopId,
        countryCode: this.order.countryCode,
        orderName: this.order.orderName,
        customerEmail: this.order.customer?.email,
        orderKey: this.order.orderKey,
      });
      if (!url) {
        EventBus.$emit(events.SNACKBAR, { message: "Kein Status-Link für diesen Shop verfügbar", type: "error" });
        return;
      }
      copyToClipboard(url);
      EventBus.$emit(events.SNACKBAR, { message: "Status-Link kopiert", type: "success" });
    },

    /* ── Bestehendes ── */
    editEmail(bool) {
      if (bool) {
        this.customEmail = this.order.customer.email;
        this.isEditEmail = true;
      } else {
        this.customEmail = "";
        this.isEditEmail = false;
      }
    },
    async saveCustomEmail() {
      this.isSavingEmail = true;
      const ordersCollectionRef = collection(db, "orders");
      const orderDoc = doc(ordersCollectionRef, this.order.uid);
      await setDoc(
        orderDoc,
        { customer: { email: this.customEmail } },
        { merge: true }
      );
      EventBus.$emit(events.SNACKBAR, {
        message: `Updated Email to ${this.customEmail}`,
        type: "success",
      });
      this.isSavingEmail = false;
      this.isEditEmail = false;
      this.order.customer.email = this.customEmail;
      this.customEmail = "";
    },
    openReproduceDialog() {
      this.showReproduceDialog = true;
    },
    openManualFulfillmentDialog() {
      this.showManualFulfillmentDialog = true;
    },
    closeManualFulfillmentDialog() {
      this.showManualFulfillmentDialog = false;
    },
    closeReproduceDialog() {
      this.showReproduceDialog = false;
    },
    copyEmailToClipboard() {
      copyToClipboard(this.order.customer.email);
    },
    closeEditOrderAddressDialog() {
      this.showEditOrderAddressDialog = false;
    },
    getDateFromNow(date) {
      return getDateFromNow(date);
    },
    getDate(date) {
      return `${dayjs(date).format("DD.MM")} at ${dayjs(date).format("hh:mm A")}`;
    },
    openOrderInShopify() {
      openOrderInShopify({
        orderId: this.order.uid,
        countryCode: this.order.countryCode,
        shopId: this.order.shopId,
      });
    },
    async cancelOrder() {
      this.isCanceling = true;
      const ordersCollectionRef = collection(db, "orders");
      const orderDoc = doc(ordersCollectionRef, this.order.uid);
      const orderRes = await getDoc(orderDoc);
      const order = orderRes.data();

      for await (const orderTask of order.tasks) {
        const tasksCollectionRef = collection(db, "tasks");
        const taskDoc = doc(tasksCollectionRef, orderTask.taskUid);
        await setDoc(taskDoc, { status: taskStatus.CANCELED }, { merge: true });
      }

      await setDoc(orderDoc, { status: orderStatus.CLOSED }, { merge: true });
      this.showCancelOrderDialog = false;
      this.isCanceling = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.ov {
  padding: 4px;
}

/* ── Alle Artworks des Kunden ── */
.ov-artworks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 10px;

  &__count {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: #7b5cd6;
    border-radius: 999px;
    padding: 1px 8px;
  }
}
.ov-artwork {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f2f7;
  border: 2px solid transparent;
  padding: 0;
  cursor: zoom-in;
  text-align: left;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  &__missing {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.35);
  }
  &__badge {
    position: absolute;
    top: 6px;
    left: 6px;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;

    &--ordered {
      background: #2e9e5b;
    }
  }
  &__meta {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 4px 6px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.65));
    color: #fff;
    font-size: 11px;
    display: flex;
    justify-content: space-between;
    gap: 4px;

    em {
      font-style: normal;
      opacity: 0.85;
    }
  }
  &--ordered {
    border-color: #2e9e5b;
  }
  &--muted {
    opacity: 0.55;
  }
  &:disabled {
    cursor: default;
  }
}
.ov-hint--error {
  color: #b71c1c;
}

/* ── Kopfzeile ── */
.ov-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  &__name {
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -0.5px;
  }
  &__sub {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
    margin-top: 2px;
  }
  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}

/* ── Status-Pills ── */
/* Hold-Chip: Fulfillment manuell angehalten */
.ov-hold-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-radius: 999px;
  padding: 3px 12px;
  background: #fff3e0;
  color: #b26a00;
  white-space: nowrap;
}

.ov-status {
  font-weight: 700;
  font-size: 12px;
  border-radius: 999px;
  padding: 3px 12px;
  background: #f1f0f5;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;

  &--sm {
    font-size: 10px;
    padding: 1px 8px;
  }
  &--ready_for_fulfill { background: #e3f2fd; color: #1565c0; }
  &--waiting_for_fulfill, &--in_progress, &--order_created { background: #fff3e0; color: #b26a00; }
  &--in_fulfill { background: #ede7f6; color: #5e35b1; }
  &--fulfilled { background: #e8f5e9; color: #2e7d32; }
  &--fulfill_error { background: #ffebee; color: #c62828; }
  &--closed { background: #f1f0f5; color: rgba(0, 0, 0, 0.5); }
}

/* ── Karten ── */
.ov-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  padding: 18px 20px;
  margin-bottom: 20px;

  &__sub {
    margin-left: auto;
    font-size: 12px;
    font-weight: 400;
    color: #888;
  }
  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 14px;
  }
  &__icon {
    color: #7b5cd6 !important;
  }
}

.ov-subhead {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.45);
  margin: 18px 0 8px;
}

.ov-hint {
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.5);
  background: #faf9fc;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 10px;
}


/* ── Tabellen ── */
.ov-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.45);
    padding: 6px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  }
  &__mono {
    font-family: Menlo, Consolas, monospace;
    font-size: 12px;
  }
}

.ov-chip {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 10px;

  &--ok { background: #e8f5e9; color: #2e7d32; }
  &--wait { background: #fff3e0; color: #b26a00; }
  &--bad { background: #ffebee; color: #c62828; }
}

.ov-qty {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &__value {
    min-width: 22px;
    text-align: center;
    font-weight: 700;
  }
}

/* ── Fulfillment ── */
.ov-fulfillment {
  margin-bottom: 16px;

  &__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }
  &__date {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
}

/* ── Customer ── */
.ov-customer {
  &__name {
    font-size: 17px;
    font-weight: 700;
  }
  &__email {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 13.5px;
    margin-top: 2px;
  }
}

.ov-order-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #faf9fc;
  }
  &__name {
    font-weight: 700;
    font-size: 13.5px;
  }
  &__date {
    margin-left: auto;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    white-space: nowrap;
  }
}

.ov-address p {
  margin-bottom: 2px;
  font-size: 13.5px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
